import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import getCroppedImg from "../helpers/cropImage";
import { Model } from "./Model";
import { useApi } from "../helpers/useApi";
import { API_URL_ENDPOINT } from "../appsetting";
import { sendError } from "../helpers/Helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";

export default function SignatureUploader({ imageURL, onUploaded, height = 60, width = 250, showEdit = false }) {
  const callApi = useApi();
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  useEffect(() => {
    setCroppedImage(imageURL)
  }, [imageURL])

  const showFilePicker = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setImageSrc(reader.result);
        setCropDialogOpen(true);
      };
    };
    input.click();
  };

  const saveCroppedImage = async () => {
    const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
    setCroppedImage(cropped);
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", cropped);
      formData.append("type", "companySignatures");

      const response = await callApi(API_URL_ENDPOINT.Document + "/upload", {
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status) {
        const documentKey = response.data?.fileKey || null;
        onUploaded(response.data);
      } else {
        throw new Error(response.message || "Something went wrong");
      }
    } catch (ex) {
      sendError(ex.message);
      return null;
    } finally {
      setCropDialogOpen(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="text-end">
      <div>
        {showEdit && (
          <p className="text-center m-0 text-gray text-xs mb-2">{`${width} px × ${height} px`}</p>
        )}
        <div
          className="relative overflow-hidden group"
          style={{
            cursor: "pointer",
            borderRadius: '8px',
            border: imageSrc ? 'none' : '2px dashed #e5e7eb',
            width: '100%' // Ensure it takes container width if needed
          }}
          onClick={showFilePicker}
        >
          <img
            className="w-full object-cover transition-opacity duration-200"
            style={{
              minHeight: height,
              display: 'block',
              backgroundImage: `url(${croppedImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            src={croppedImage || "https://placehold.co/400x600?text=Upload+Cover"}
            alt="Cover Preview"
          />
          <div
            className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <FontAwesomeIcon icon={faPencil} className="text-white text-lg" />
          </div>
        </div>

        {showEdit && (
          <div className="text-center mt-2 cursor-pointer">
            <small
              className="font-bold text-accent text-xs hover:underline"
              onClick={showFilePicker}
            >
              Change Image
            </small>
          </div>
        )}
      </div>

      <Model show={cropDialogOpen} size="lg">
        <div className="p-4">
          <h6 className="mb-4 font-bold text-lg text-dark">Crop Image</h6>
          <div className="relative h-96 bg-gray-900 rounded-lg overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={width / height}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div className="mt-4 px-2">
            <label className="block text-sm text-gray font-medium mb-1">Zoom</label>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e) => setZoom(e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-accent"
            />
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-lightGray">
            <button
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => setCropDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              disabled={isSubmitting}
              onClick={saveCroppedImage}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-accent to-success rounded-lg hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 transition-all"
            >
              {isSubmitting ? 'Uploading...' : 'Done'}
            </button>
          </div>
        </div>
      </Model>
    </div>
  );
}
